import React from 'react';
import { motion } from 'framer-motion';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'elevated';
  onClick?: () => void;
  style?: React.CSSProperties;
  overlay?: React.ReactNode;
  backgroundImage?: string;
  backgroundPosition?: string;
}

/**
 * Card component with gradient styling and dark theme
 * Supports elevated variant for featured content
 */
export const Card: React.FC<CardProps> = ({
  children,
  className = '',
  variant = 'default',
  onClick,
  style,
  overlay,
  backgroundImage,
  backgroundPosition = 'center',
}) => {
  // Component cards use bg1 (2nd darkest) - hierarchy: black (screen) -> bg1 (cards) -> bg3 (buttons on cards)
  // Enhanced styling for "main character" appearance
  const baseClasses = 'rounded-2xl p-6 bg-grey-bg1 relative overflow-hidden min-h-0';
  
  const variantClasses = {
    default: 'border border-white-p40/10',
    elevated: 'border border-white-p40/20', // Subtle border for elevated cards
  };

  // No glow effect
  const glowEffect = {};

  // No gradient overlay - removed to show video background clearly
  const gradientOverlay = '';


  // Determine if card should fill height
  const shouldFillHeight = className.includes('h-full') || style?.height === '100%' || style?.height?.toString().includes('100%');
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className={`${baseClasses} ${variantClasses[variant]} ${gradientOverlay} ${className} relative`}
      onClick={onClick}
      style={{
        ...(onClick ? { cursor: 'pointer' } : {}),
        ...glowEffect,
        ...(shouldFillHeight ? { height: '100%' } : {}),
        ...style,
      }}
    >
      {/* Image background for elevated variant */}
      {variant === 'elevated' && (
        <>
          <div
            className="absolute inset-0 w-full h-full rounded-2xl"
            style={{
              backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'url(/affirmation_bg.png)',
              backgroundSize: 'cover',
              backgroundPosition: backgroundPosition,
              backgroundRepeat: 'no-repeat',
              zIndex: 1,
            }}
          />
          {/* Black overlay */}
          <div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 2,
            }}
          />
        </>
      )}

      {overlay}
      {children}
    </motion.div>
  );
};

export default Card;
